import React, { useEffect, useRef, useState } from 'react';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import { arrowHeadLeftSVG } from '../../../lib/allCustomSVGs';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import beautifyLabel from '../../../lib/helper-functions/beautifyLabel';
import makeFirstLetterCapital from '../../../lib/helper-functions/makeFirstLetterCapital';
import isNotEmptyString from '../../../lib/helper-functions/isNotEmptyString';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import { RootState } from '../../../store/store';
import { useSelector } from 'react-redux';

type actionPropTypes =
  | '/element/enable'
  | '/element/disable'
  | '/element/pause'
  | '/element/resume'
  | '/element/reject'
  | '/element/unarchive'
  | '/select'
  | '/unselect';

type Props = {
  elementData: any;
  recommendationCurrentStatus?: string;
  apiCallPendingElementID: string | null;
  apiCallPendingElementAction: string | null;
  onToggleStyle?: (
    tab: string,
    elementId: string,
    styleId: string,
    newStatus: boolean
  ) => Promise<void>;
  onAction?: (
    element: any,
    currentStatus: string,
    action: actionPropTypes,
    targetStatus: string
  ) => Promise<void>;
};

const UIRecommendationsElement = ({
  elementData,
  recommendationCurrentStatus,
  apiCallPendingElementID,
  apiCallPendingElementAction,
  onToggleStyle,
  onAction,
}: Props) => {
  const [isItemExpanded, setIsItemExpanded] = useState<boolean>(false);
  const { t } = useTranslation();

  const isDarkMode = useSelector((state: RootState) => state.UI.theme.dark);

  /* ---close window logic start-----*/
  const containerRef = useRef<HTMLDivElement>(null); // Ref for the component container

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsItemExpanded(false);
      }
    };

    if (isItemExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isItemExpanded]);
  /* ---close window logic end-----*/

  // Converts kebab-case CSS properties to camelCase for React inline styling
  function toCamelCase(cssProperty: string): string {
    if (typeof cssProperty !== 'string') return cssProperty; // fail-safe check
    return cssProperty?.replace(/-([a-z])/g, (_, letter) =>
      letter?.toUpperCase()
    );
  }

  const renderScenarios = (scenarios: any) => {
    if (!scenarios) {
      return null;
    }

    if (!scenarios || Object.keys(scenarios)?.length === 0) {
      return null;
    }

    return (
      <div className='uiRecommendationsElement__scenarios'>
        <div className='uiRecommendationsElement__scenarios--heading'>
          <span>{t('AppliedScenarios')}</span>
        </div>
        {Object.keys(scenarios)?.map((scenarioKey) => {
          const scenarioValue = scenarios[scenarioKey];

          if (!scenarioValue || Object.keys(scenarioValue)?.length === 0) {
            return null;
          }

          return (
            <div
              key={scenarioKey}
              className='uiRecommendationsElement__scenario'
            >
              <div className='uiRecommendationsElement__scenario--title'>
                <span>{makeFirstLetterCapital(scenarioKey)}</span>
              </div>
              <div className='uiRecommendationsElement__scenario--items'>
                {/* Check if scenarioValue is an array */}
                {Array.isArray(scenarioValue)
                  ? // If it's an array, render each element as a list item
                    scenarioValue?.map((item, index) => (
                      <div
                        key={index}
                        className='uiRecommendationsElement__scenario--items-item'
                      >
                        <div className='uiRecommendationsElement__scenario--items-item-value'>
                          {makeFirstLetterCapital(item)}
                          {scenarioValue?.length > 1 &&
                          index !== scenarioValue?.length - 1
                            ? ','
                            : ''}
                        </div>
                      </div>
                    ))
                  : // Otherwise, treat it as an object and render its properties
                    Object.keys(scenarioValue)?.map((key) => {
                      const renderScenarioValue = scenarioValue[key];
                      return (
                        <div
                          key={key}
                          className='uiRecommendationsElement__scenario--items-item'
                        >
                          <div className='uiRecommendationsElement__scenario--items-item-key'>
                            {makeFirstLetterCapital(key)}:
                          </div>
                          <div className='uiRecommendationsElement__scenario--items-item-value'>
                            {renderScenarioValue}
                          </div>
                        </div>
                      );
                    })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderImpact = (impact: null | number, conversion: null | number) => {
    if (recommendationCurrentStatus !== 'active') {
      return null;
    } else if (impact === null) {
      return (
        <div className='uiRecommendationsElement__impact'>
          <div className='uiRecommendationsElement__impact--item'>
            <span className='uiRecommendationsElement__impact--item-label'>
              {t('Impact')}
            </span>
            <span
              style={{
                minWidth: '7rem',
                minHeight: '1.5rem',
                borderRadius: '0.5rem',
              }}
              className={`uiRecommendationsElement__impact--item-value ${
                !isDarkMode
                  ? 'skeleton-loader-light'
                  : isDarkMode
                  ? 'skeleton-loader-dark'
                  : ''
              }`}
            >
              {/* {`loading...`} */}
            </span>
          </div>
          <div className='uiRecommendationsElement__impact--item'>
            <span className='uiRecommendationsElement__impact--item-label'>
              {t('CTR')}
            </span>
            <span
              style={{
                minWidth: '7rem',
                minHeight: '1.5rem',
                borderRadius: '0.5rem',
              }}
              className={`uiRecommendationsElement__impact--item-value ${
                !isDarkMode
                  ? 'skeleton-loader-light'
                  : isDarkMode
                  ? 'skeleton-loader-dark'
                  : ''
              }`}
            >
              {/* {`loading...`} */}
            </span>
          </div>
        </div>
      );
    } else if (typeof impact === 'number') {
      return (
        <div className='uiRecommendationsElement__impact'>
          <div className='uiRecommendationsElement__impact--item'>
            <span className='uiRecommendationsElement__impact--item-label'>
              {`${t('Impact')}:`}
            </span>
            <span className={`uiRecommendationsElement__impact--item-value `}>
              {`${impact}%`}
            </span>
          </div>
          <div className='uiRecommendationsElement__impact--item'>
            <span className='uiRecommendationsElement__impact--item-label'>
              {`${t('CTR')}:`}
            </span>
            <span className={`uiRecommendationsElement__impact--item-value `}>
              {`${conversion}%`}
            </span>
          </div>
        </div>
      );
    }
  };

  const renderCurrentPreview = (
    content: any,
    styles: Record<string, string>
  ) => {
    // List of CSS properties to exclude from inline styles
    const excludingProperties = ['height', 'margin', 'width', 'position'];

    // Safely filter and convert styles to React-compatible format
    const filteredStyles = Object.fromEntries(
      Object.entries(styles || {}) // ensure styles is a valid object
        ?.filter(
          ([key]) => !excludingProperties?.includes(key) // remove excluded props
        )
        ?.map(([key, value]) => [toCamelCase(key), value]) // convert keys to camelCase
    );

    // Determine the text to render inside the preview box
    if (isNotEmptyString(content)) {
      return (
        <div
          style={filteredStyles}
          className='uiRecommendationsElement__current--preview-box'
        >
          {content}
        </div>
      );
    } else {
      return (
        <div
          style={filteredStyles}
          className='uiRecommendationsElement__current--preview-box'
        >
          {t('Example')}
        </div>
      );
    }
  };

  const renderRecommendedPreview = (stylesArray: any[]) => {
    // List of CSS properties to exclude from inline styles
    const excludingProperties = ['height', 'margin', 'width', 'position'];

    // Filter active styles and exclude specified properties
    const activeStyles = (stylesArray || []).filter(
      (style) =>
        style.is_active && !excludingProperties.includes(style.property_type)
    );

    // Convert remaining styles to React-compatible format
    const reactStyles = activeStyles.reduce(
      (acc: Record<string, string>, style) => {
        const camelCaseKey = toCamelCase(style.property_type);
        acc[camelCaseKey] = style.value;
        return acc;
      },
      {}
    );

    // Determine content to display
    const content = isNotEmptyString(elementData?.content)
      ? elementData.content
      : t('Example');

    return (
      <div
        style={reactStyles}
        className='uiRecommendationsElement__recommended--preview-box'
      >
        {content}
      </div>
    );
  };

  const renderProperty = (propertyData: any) => {
    const isProcessing =
      apiCallPendingElementID === elementData.element_id &&
      (apiCallPendingElementAction === '/select' ||
        apiCallPendingElementAction === '/unselect');

    return (
      <div
        key={propertyData?.id}
        className='uiRecommendationsElement__properties--item'
      >
        <div className='uiRecommendationsElement__properties--item-top'>
          <div className='uiRecommendationsElement__properties--item-top-left'>
            <h3>{beautifyLabel(propertyData?.property_type)}:</h3>
            <span>{propertyData?.value}</span>
          </div>
          <div className='uiRecommendationsElement__properties--item-top-right'>
            {
              <ToggleSwitch
                disabled={isProcessing}
                isOn={propertyData?.is_active}
                onClick={() => {
                  if (!recommendationCurrentStatus) return;
                  const newStatus = !propertyData.is_active;
                  onToggleStyle?.(
                    recommendationCurrentStatus,
                    elementData.element_id,
                    propertyData.id,
                    newStatus
                  );
                }}
              />
            }
            {isProcessing && <div className='toggle-loading-indicator' />}
          </div>
        </div>
        <div className='uiRecommendationsElement__properties--item-bottom'>
          {propertyData?.description}
        </div>
      </div>
    );
  };

  const renderButtons = () => {
    const handleAction = async (
      action: actionPropTypes,
      targetStatus: string
    ) => {
      if (!recommendationCurrentStatus) return;

      await onAction?.(
        elementData,
        recommendationCurrentStatus,
        action,
        targetStatus
      );
    };

    if (recommendationCurrentStatus === 'active') {
      return (
        <div className='uiRecommendationsElement__actions'>
          <div className='uiRecommendationsElement__actions--btn'>
            <MainSquareButton
              value={t('Pause')}
              onClick={() => handleAction('/element/pause', 'paused')}
              loading={
                apiCallPendingElementID === elementData.element_id &&
                apiCallPendingElementAction === '/element/pause'
              }
              disabled={!!apiCallPendingElementID}
              type='button'
              buttonType='filter'
            />
          </div>
          <div className='uiRecommendationsElement__actions--btn'>
            <MainSquareButton
              value={t('Reject')}
              onClick={() => handleAction('/element/reject', 'rejected')}
              loading={
                apiCallPendingElementID === elementData.element_id &&
                apiCallPendingElementAction === '/element/reject'
              }
              disabled={!!apiCallPendingElementID}
              type='button'
              buttonType='danger'
            />
          </div>
        </div>
      );
    }

    if (recommendationCurrentStatus === 'paused') {
      return (
        <div className='uiRecommendationsElement__actions'>
          <div className='uiRecommendationsElement__actions--btn'>
            <MainSquareButton
              value={t('Resume')}
              onClick={() => handleAction('/element/resume', 'active')}
              loading={
                apiCallPendingElementID === elementData.element_id &&
                apiCallPendingElementAction === '/element/resume'
              }
              disabled={!!apiCallPendingElementID}
              type='button'
              buttonType='success'
            />
          </div>
          <div className='uiRecommendationsElement__actions--btn'>
            <MainSquareButton
              value={t('Reject')}
              onClick={() => handleAction('/element/reject', 'rejected')}
              loading={
                apiCallPendingElementID === elementData.element_id &&
                apiCallPendingElementAction === '/element/reject'
              }
              disabled={!!apiCallPendingElementID}
              type='button'
              buttonType='danger'
            />
          </div>
        </div>
      );
    }

    if (recommendationCurrentStatus === 'rejected') {
      return (
        <div className='uiRecommendationsElement__actions'>
          <div className='uiRecommendationsElement__actions--btn'>
            <MainSquareButton
              value={t('Unarchive')}
              onClick={() => handleAction('/element/unarchive', 'pending')}
              loading={
                apiCallPendingElementID === elementData.element_id &&
                apiCallPendingElementAction === '/element/unarchive'
              }
              disabled={!!apiCallPendingElementID}
              type='button'
              buttonType='warning'
            />
          </div>
        </div>
      );
    }

    if (recommendationCurrentStatus === 'pending') {
      return (
        <div className='uiRecommendationsElement__actions'>
          <div className='uiRecommendationsElement__actions--btn'>
            <MainSquareButton
              value={t('Select')}
              onClick={() => handleAction('/element/enable', 'active')}
              loading={
                apiCallPendingElementID === elementData.element_id &&
                apiCallPendingElementAction === '/element/enable'
              }
              disabled={!!apiCallPendingElementID}
              buttonType='success'
              type='button'
            />
          </div>
          <div className='uiRecommendationsElement__actions--btn'>
            <MainSquareButton
              value={t('Reject')}
              onClick={() => handleAction('/element/reject', 'rejected')}
              loading={
                apiCallPendingElementID === elementData.element_id &&
                apiCallPendingElementAction === '/element/reject'
              }
              disabled={!!apiCallPendingElementID}
              type='button'
              buttonType='danger'
            />
          </div>
        </div>
      );
    }
  };

  return (
    <div
      ref={containerRef}
      data-is-recommendation-element-expanded={isItemExpanded.toString()}
      className='uiRecommendationsElement'
    >
      {/* top section */}
      <div className='uiRecommendationsElement__top'>
        <div className='uiRecommendationsElement__top--title'>
          <h2>{beautifyLabel(elementData?.element)}</h2>
          <span>{beautifyLabel(elementData?.type, true)}</span>
        </div>
        <div
          onClick={() => setIsItemExpanded(!isItemExpanded)}
          className='uiRecommendationsElement__top--icon'
        >
          <CustomSVGs svg={arrowHeadLeftSVG} />
        </div>
      </div>

      {/* Impact */}
      {renderImpact(
        elementData?.impact,
        elementData?.control_group_conversion_rate
      )}

      {/* Current Element */}
      <div className='uiRecommendationsElement__current'>
        <div className='uiRecommendationsElement__current--preview'>
          <div className='uiRecommendationsElement__current--preview-heading'>
            <span>{`${t('CurrentElement')} `}</span>
          </div>
          {renderCurrentPreview(
            elementData?.content,
            elementData?.current_styles
          )}
        </div>
      </div>

      {/* Recommended */}
      <div className='uiRecommendationsElement__recommended'>
        <div className='uiRecommendationsElement__recommended--preview'>
          <div className='uiRecommendationsElement__recommended--preview-heading'>
            <span>{`${t('RecommendedElement')} `}</span>
          </div>
          {renderRecommendedPreview(elementData?.styles)}
        </div>
      </div>

      {/* Expanding Part */}
      <AnimatePresence>
        {isItemExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
          >
            {/* Scenarios*/}
            {renderScenarios(elementData?.senario)}

            {/* Recommended Properties */}
            {elementData?.styles && elementData?.styles?.length > 0 && (
              <div className='uiRecommendationsElement__properties'>
                <div className='uiRecommendationsElement__properties--heading'>
                  <span>{`${t('RecommendedProperties')} `}</span>
                </div>
                {elementData?.styles?.map((property: any) => {
                  return renderProperty(property);
                })}
              </div>
            )}

            {/* Action buttons recommendationCurrentStatus*/}
            <div className='uiRecommendationsElement__actions'>
              {renderButtons()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UIRecommendationsElement;
